// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullAdminRegatta,
  $ModelUpdateParams,
} from 'common.data';

// Global Enums
import {
  RegattaPaymentStatus,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Link,
} from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  Col,
  InputGroup,
  InputGroupText,
  Label,
  Row,
} from 'reactstrap';
import {
  AvForm,
  AvInput,
} from 'availity-reactstrap-validation';
import {
  CommonDialog,
  DeleteDialog,
  FormDialog,
  InputDoubleButton,
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
} from '@vroskus/library-helpers/dist/browser';
import {
  getRegionSeasonUrl,
  getShortItemUrl,
} from 'common.helpers/dist/browser';
import {
  regattaRegistrationStatusLabel,
} from 'common.language';
import {
  defaultUpsertItem,
} from '../../../../helpers';
import config from '../../../../config';

// Components
import PaymentDialog from '../../../dialog/PaymentDialog';
import RegattaSuperFields from '../../../fields/RegattaSuperFields';

// Services
import Services from '../../../../services';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

export type $OwnProps = $Translate & {
  id: string;
  item: $FullAdminRegatta;
};

export type $StateProps = {
  auth: $ReducerState<'auth'>;
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

class RegattaStatusSectionView extends React.Component<$Props> {
  isPayable(): boolean {
    const {
      item,
    } = this.props;

    const isPublicButPageEnabled: boolean = item.Public === false && item.PageEnabled === true;
    const isPageDisabledButPublic: boolean = item.Public === true && item.PageEnabled === false;

    return isPublicButPageEnabled || isPageDisabledButPublic;
  }

  async updateItem(newValues: $ModelUpdateParams<'Regatta'>): Promise<void> {
    const {
      item,
    } = this.props;

    await defaultUpsertItem({
      action: async () => {
        const params = cleanFormValues<$FullAdminRegatta>({
          ...item,
          ...newValues,
        });

        await Services.Api.updateRegatta(params);
      },
    });
  }

  isSuperUser(): boolean {
    const {
      auth,
    } = this.props;

    return auth.user !== null && auth.user.Super === true;
  }

  renderButtonDefaultState() {
    const {
      item,
      t,
    } = this.props;

    return (
      <PaymentDialog
        id={'publish-regatta-dialog'}
        itemId={item.id}
      >
        <Button
          className={'input-group w-auto'}
          color={'success'}
          id={'publish-regatta-button'}
          outline
        >
          {t('Publish regatta')}
        </Button>
      </PaymentDialog>
    );
  }

  renderButtonPendingState() {
    const {
      t,
    } = this.props;

    return (
      <Button
        className={'input-group'}
        color={'warning'}
        disabled
        id={'awaiting-payment-button'}
        outline
      >
        {t('Awaiting payment')}
      </Button>
    );
  }

  renderButtonFulfilledState() {
    const {
      item,
      t,
    } = this.props;

    return (
      <InputDoubleButton
        labelFalse={t('Hidden')}
        labelTrue={t('Visible')}
        name={'Public'}
        onChange={(value) => this.updateItem({
          id: item.id,
          Public: value,
        })}
        value={item.Public}
      />
    );
  }

  renderPublishButton() {
    const {
      item,
    } = this.props;

    if (item.RegattaPayment) {
      if (item.RegattaPayment.Status === RegattaPaymentStatus.pending) {
        return this.renderButtonPendingState();
      }

      if (item.RegattaPayment.Status === RegattaPaymentStatus.fulfilled) {
        return this.renderButtonFulfilledState();
      }
    }

    return this.renderButtonDefaultState();
  }

  renderVisibilityPart() {
    const {
      item,
      t,
    } = this.props;

    const isPayable: boolean = this.isPayable();

    return (
      <>
        <div>
          <p className={'form-label'}>
            {t('Visibility')}
          </p>
          <div className={'mb-3'}>
            {isPayable === true ? (
              <span className={'h3 text-danger'}>
                {t('Unpublished')}
              </span>
            ) : (
              <div className={'h3 text-success'}>
                {t('Public')}
                {item.Canceled === true && (
                  <span className={'text-danger'}>
                    {` (${t('Canceled')})`}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={'form-group d-flex flex-row mb-3'}>
          {this.renderPublishButton()}
          {this.isSuperUser() === true && (
            <FormDialog
              fields={(
                <RegattaSuperFields item={item} />
              )}
              id={'regatta-super-user-control-dialog'}
              labelCloseButton={t('Close')}
              labelSaveButton={t('Save')}
              onAction={(values) => this.updateItem({
                ...values,
                id: item.id,
              })}
              subTitle={t('Super user control')}
              title={t('Regatta')}
            >
              <Button
                className={'ml-1'}
                color={'secondary'}
                id={'regatta-super-user-control-button'}
                outline
                size={'sm'}
              >
                <em className={'fa icon-lock'} />
              </Button>
            </FormDialog>
          )}
        </div>
      </>
    );
  }

  renderRegistrationPart() {
    const {
      item,
      t,
    } = this.props;

    return (
      <>
        <div>
          <p className={'form-label'}>
            {t('Registration')}
          </p>
          <div className={'h3'}>
            {t(regattaRegistrationStatusLabel[item.RegistrationStatus])}
          </div>
        </div>
        <div className={'mb-3'}>
          <InputDoubleButton
            labelFalse={t('Off')}
            labelTrue={t('Enabled')}
            name={'RegistrationEnabled'}
            onChange={(value) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$FullAdminRegatta>({
                  ...item,
                  RegistrationEnabled: value,
                });

                await Services.Api.updateRegatta(params);
              },
            })}
            value={item.RegistrationEnabled}
          />
        </div>
      </>
    );
  }

  renderRegattaPagePartInvoiceButton() {
    const {
      auth,
      item,
      t,
    } = this.props;

    if (item.Invoice !== null) {
      return (
        <Button
          className={'mr-1'}
          color={'secondary'}
          href={item.Invoice}
          id={'regatta-invoice-button'}
          outline
          size={'sm'}
          target={'_blank'}
        >
          <em className={'far fa-file-pdf mr-1'} />
          {t('Invoice')}
        </Button>
      );
    }

    if (auth.user !== null) {
      const {
        user,
      } = auth;

      return (
        <CommonDialog
          content={(
            <CardBody>
              <div className={'mb-3'}>
                {t('By this you confirm that you need an invoice')}
              </div>
              <Card className={'mb-3'}>
                <CardBody>
                  <Row>
                    <Col>
                      <div>
                        {user.InvoiceName}
                      </div>
                      <div>
                        {`${user.InvoiceNo} / ${user.InvoiceVat}`}
                      </div>
                      <div>
                        <span>{`${t('Address')}: `}</span>
                        {user.InvoiceAddress}
                      </div>
                    </Col>
                    <Col className={'text-right'}>
                      <Link
                        className={'mr-1 btn btn-outline-warning btn-sm'}
                        title={t('Invoice details')}
                        to={'/profile'}
                      >
                        {t('Edit')}
                      </Link>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </CardBody>
          )}
          id={'regatta-request-invoice-dialog'}
          labelCloseButton={t('Close')}
          labelConfirmButton={t('Confirm')}
          onAction={() => this.updateItem({
            id: item.id,
            InvoiceAdminId: user.id,
          })}
          title={t('Need confirmation')}
        >
          <Button
            className={'mr-1'}
            color={'secondary'}
            disabled={item.InvoiceAdminId !== null}
            id={'regatta-request-invoice-button'}
            size={'sm'}
          >
            {t('Request invoice')}
          </Button>
        </CommonDialog>
      );
    }

    return null;
  }

  renderRegattaPagePartCancelButton() {
    const {
      item,
      t,
    } = this.props;

    return (item.Canceled === true ? (
      <Button
        color={'secondary'}
        id={'regatta-undo-cancel-button'}
        onClick={() => this.updateItem({
          Canceled: false,
          id: item.id,
        })}
        outline
        size={'sm'}
      >
        {t('Undo cancel')}
      </Button>
    ) : (
      <DeleteDialog
        content={t('Do you really want to cancel regatta?')}
        id={'regatta-cancel-dialog'}
        labelCloseButton={t('Close')}
        labelDeleteButton={t('Confirm cancellation')}
        onAction={() => this.updateItem({
          Canceled: true,
          id: item.id,
        })}
        subTitle={item.Name}
        title={t('Cancel regatta')}
      >
        <Button
          color={'danger'}
          id={'regatta-cancel-button'}
          outline
          size={'sm'}
        >
          {t('Cancel regatta')}
        </Button>
      </DeleteDialog>
    ));
  }

  renderRegattaPagePart() {
    const {
      item,
      main,
      t,
    } = this.props;

    const {
      regions,
    } = main;

    const regattaUrl = getShortItemUrl({
      basePath: '/regatta',
      item,
      mainDomain: config.DOMAIN,
      regions,
    });

    const isPayable: boolean = this.isPayable();

    const regionSeasonUrl: string = getRegionSeasonUrl({
      mainDomain: config.DOMAIN,
      regions: main.regions,
      RegionSeasonId: item.RegionSeasonId,
    });

    return (
      <>
        <div>
          <p className={'form-label'}>
            {t('Regatta page')}
          </p>
          <div className={'mb-3'}>
            <a
              className={'h4'}
              href={regattaUrl}
              rel={'noreferrer'}
              target={'_blank'}
            >
              {regattaUrl}
            </a>
            <FormDialog
              fields={(
                <Row>
                  <Col>
                    <Label>
                      {t('Page address')}
                    </Label>
                    <InputGroup className={'mb-2'}>
                      <InputGroupText>
                        {regionSeasonUrl}
                      </InputGroupText>
                      <AvInput
                        name={'Slug'}
                        value={item.Slug}
                      />
                    </InputGroup>
                  </Col>
                </Row>
              )}
              id={'regatta-page-dialog'}
              labelCloseButton={t('Close')}
              labelSaveButton={t('Save')}
              onAction={(values) => this.updateItem({
                id: item.id,
                Slug: values.Slug as string,
              })}
              subTitle={''}
              title={t('Regatta page')}
            >
              <Button
                className={'ml-1'}
                color={'warning'}
                disabled={isPayable && this.isSuperUser() === false}
                id={'regatta-page-button'}
                outline
                size={'xs'}
              >
                {t('Edit')}
              </Button>
            </FormDialog>
          </div>
        </div>
        <div className={'mb-3'}>
          <Row>
            <Col xs={7}>
              <InputDoubleButton
                labelFalse={t('Hidden')}
                labelTrue={t('Visible on regattas list page')}
                name={'ListEnabled'}
                onChange={(value) => this.updateItem({
                  id: item.id,
                  ListEnabled: value,
                })}
                value={item.ListEnabled}
              />
            </Col>
            <Col
              className={'d-flex justify-content-end'}
              xs={5}
            >
              {this.renderRegattaPagePartInvoiceButton()}
              {this.renderRegattaPagePartCancelButton()}
            </Col>
          </Row>
        </div>
      </>
    );
  }

  renderForm() {
    return (
      <AvForm>
        <Row>
          <Col
            className={'d-flex flex-column justify-content-between'}
            md={3}
            xs={7}
          >
            {this.renderVisibilityPart()}
          </Col>
          <Col
            className={'d-flex flex-column justify-content-between'}
            md={3}
            xs={5}
          >
            {this.renderRegistrationPart()}
          </Col>
          <Col
            md={6}
          >
            {this.renderRegattaPagePart()}
          </Col>
        </Row>
      </AvForm>
    );
  }

  render() {
    const {
      id,
      t,
    } = this.props;

    return (
      <Card id={id}>
        <SectionHeader
          bottomBorder
          iconClass={'icon-compass'}
          title={t('Regatta status')}
        />
        <CardBody className={'py-2'}>
          {this.renderForm()}
        </CardBody>
      </Card>
    );
  }
}

export default RegattaStatusSectionView;
